import {
    AsyncLoadable,
    CollectionBase,
    FocusableProps,
    FocusStrategy,
    InputBase,
    LabelableProps,
    MultipleSelection,
    TextInputBase,
    Validation,
} from '@react-types/shared';
import type { Key, OverlayTriggerProps } from 'react-stately';
import { MenuTriggerState, TreeState, useMenuTriggerState, useTreeState } from 'react-stately';
import { useState } from 'react';
import { FormValidationState, useFormValidationState } from '@react-stately/form';

export interface MultiSelectStateProps<T>
    extends CollectionBase<T>,
        AsyncLoadable,
        Omit<InputBase, 'isReadOnly'>,
        Validation<T>,
        LabelableProps,
        TextInputBase,
        MultipleSelection,
        FocusableProps,
        OverlayTriggerProps {
    /**
     * Whether the menu should automatically flip direction when space is limited.
     * @default true
     */
    shouldFlip?: boolean;

    /**
     * Открывать ли Select с пустой коллекцией
     */
    allowEmptyCollection?: boolean;
}

export interface MultiSelectState<T> extends TreeState<T>, MenuTriggerState, FormValidationState {
    /** Whether the select is currently focused. */
    isFocused: boolean;

    /** Sets whether the select is focused. */
    setFocused(isFocused: boolean): void;
}

export function useMultiSelectState<T extends object>(props: MultiSelectStateProps<T>): MultiSelectState<T> {
    const triggerState = useMenuTriggerState(props);
    const treeState = useTreeState({
        ...props,
        // Добавляем это свойство, чтобы не потерять поведение обычного селекта:
        // вызвать onChange при клике на выбранный элемент
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error Оно прокидывается в `useMultipleSelectionState`, но не наследуется в `TreeStateProps`
        allowDuplicateSelectionEvents: true,
        onSelectionChange: keys => {
            if (props.onSelectionChange != null) {
                if (keys === 'all') {
                    // This may change back to "all" once we will implement async loading of additional
                    // items and differentiation between "select all" vs. "select visible".
                    props.onSelectionChange(new Set(treeState.collection.getKeys()));
                } else {
                    props.onSelectionChange(keys);
                }
            }

            // Multi select stays open after item selection
            if (props.selectionMode === 'single') {
                triggerState.close();
            }
        },
    });

    const [isFocused, setFocused] = useState(false);
    const validationState = useFormValidationState<Set<Key>>({
        ...props,
        validate: _ => true,
        value: treeState.selectionManager.selectedKeys,
    });

    return {
        ...treeState,
        ...triggerState,
        ...validationState,
        close() {
            triggerState.close();
        },
        open() {
            // Don't open if the collection is empty.
            if (props.allowEmptyCollection || treeState.collection.size !== 0) {
                triggerState.open();
            }
        },
        toggle(focusStrategy: FocusStrategy) {
            if (props.allowEmptyCollection || treeState.collection.size !== 0) {
                triggerState.toggle(focusStrategy);
            }
        },
        isFocused,
        setFocused,
    };
}
